import axios from 'axios';
import toast from 'react-hot-toast';

import { env } from '@/common/config';
import oneSignalService from '@/common/services/oneSignalService';

/**
 * Clear OneSignal identity before wiping the session so the next visitor
 * on this browser is not linked to the previous user.
 */
async function clearOneSignalSession() {
  try {
    await oneSignalService.logout();
  } catch (error) {
    console.error('[OneSignal] session clear failed:', error);
  }
}

/**
 * Single pre-configured API client instance.
 * @see docs/api-layer.md
 * @type {import('axios').AxiosInstance}
 */
export const apiClient = axios.create({
  baseURL: env.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Indicates whether a token refresh is currently in progress.
 * @type {boolean}
 */
let isRefreshing = false;

/**
 * Queue to hold pending requests while a token refresh is in progress.
 * @type {Array<{resolve: (value: string | null) => void, reject: (reason?: any) => void}>}
 */
let failedQueue = [];

/**
 * Process the queue of pending requests once token refresh completes or fails.
 * @param {Error | null} error - The error if refresh failed, or null if successful.
 * @param {string | null} [token=null] - The new access token if refresh was successful.
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Executes the token refresh flow.
 * - Sends current refresh token to the API
 * - Receives new access and refresh tokens
 * - Updates both in storage
 * - Clears session and redirects to login on failure
 * @returns {Promise<string>} The new access token.
 * @throws {Error} If refresh token is unavailable or refresh request fails.
 */
export const refreshTokenFlow = async () => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    await clearOneSignalSession();
    localStorage.clear();
    window.location.href = '/login';
    toast.error('Session expired. Please log in again.');
    throw new Error('No refresh token available');
  }

  try {
    // console.log("Calling Refresh API");
    // Note: We use axios directly here, NOT apiClient, to avoid interceptor loops
    const response = await axios.post(
      `${env.apiBaseUrl}/v1/auth/token/refresh/`,
      {
        refresh: refreshToken,
      },
    );

    const { access, refresh } = response.data;

    if (!access || !refresh) {
      throw new Error('Invalid token response from server');
    }

    // Replace old access and refresh tokens with the new ones immediately
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${access}`;
    // console.log("Refresh Success");

    return access;
  } catch (error) {
    // console.log("Refresh Failed - Logging Out");
    // If refresh fails (401/403/invalid token), clear session and force login
    await clearOneSignalSession();
    localStorage.clear();
    window.location.href = '/login';
    toast.error('Unable to refresh session. Please try again.');
    throw error;
  }
};

apiClient.interceptors.request.use(
  /**
   * @param {import('axios').InternalAxiosRequestConfig} config
   * @returns {import('axios').InternalAxiosRequestConfig}
   */
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Globally apply l-trim and r-trim to search query params
    if (config.params) {
      ['search', 'q', 'query'].forEach((key) => {
        if (typeof config.params[key] === 'string') {
          config.params[key] = config.params[key].trim();
        }
      });
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

apiClient.interceptors.response.use(
  (response) => response,
  /**
   * @param {import('axios').AxiosError} error
   * @returns {Promise<any>}
   */
  async (error) => {
    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (!error.response) {
      // React Query aborts via `signal` on StrictMode remount and route change.
      // Canceled requests have no response; they are not a network failure.
      const canceled =
        axios.isCancel(error) ||
        error.code === 'ERR_CANCELED' ||
        error.name === 'CanceledError';
      if (!canceled) {
        toast.error('Something went wrong. Please retry.', {
          id: 'network-error',
        });
      }
      return Promise.reject(error);
    }

    const isAuthPath =
      originalRequest.url &&
      (originalRequest.url.includes('/accounts/login/') ||
        originalRequest.url.includes('/accounts/register/') ||
        originalRequest.url.includes('/accounts/password/') ||
        originalRequest.url.includes('/accounts/otp/') ||
        originalRequest.url.includes('/accounts/logout/') ||
        originalRequest.url.includes('/auth/') ||
        originalRequest.url.includes('/token/refresh/'));

    const isTokenError =
      error.response.status === 401 ||
      (error.response.status === 403 &&
        error.response.data &&
        error.response.data.code === 'token_not_valid');

    // If token error, and we haven't already retried this request, and it's not an auth endpoint
    if (
      isTokenError &&
      // @ts-ignore - custom property
      !originalRequest._retry &&
      !isAuthPath
    ) {
      if (isRefreshing) {
        // Queue the request until refresh is done
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      // console.log("JWT Refresh Triggered");
      // @ts-ignore
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const token = await refreshTokenFlow();
        // Update the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${token}`;
        processQueue(null, token);
        // console.log("Retrying Original Request");
        // Retry the original request
        return apiClient(originalRequest);
      } catch (err) {
        processQueue(err, null);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);
